/** FRONT-3223: the interactive question card of the Build window before FRONT-3138, kept for `coding-session-modal-legacy.tsx` (`eddy.ui-refactoring` off). */
import { useMemo, useState } from 'react';
import {
  ChatCircleText,
  CheckCircle,
  CircleNotch,
  FastForward,
  HandPalm,
  PaperPlaneTilt,
  XCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { BudgetRefusedError } from '~/features/limits/budget-refusal';
import { AnsweredView, OTHER, PendingView } from '~/features/chat/questions-card';
import type {
  CodingQuestionAnswer,
  CodingStatus,
  TranscriptItem,
} from './coding-events';

type QuestionsItem = Extract<TranscriptItem, { kind: 'questions' }>;

/** What a card can send. FRONT-2478: "stop asking" left the card for the
 *  Builder's interview bar (guided-interview-bar-view.tsx), so a card only
 *  ever answers, skips or (FRONT-2661) dismisses — the stop body is sent
 *  from there. */
type AnswerBody = { answers: CodingQuestionAnswer[] } | { skip: true } | { dismiss: true };

/** Pure — which submitting state a body maps to. Exported so it's
 *  unit-testable without mounting the component (FRONT-1160 pr-panel
 *  finding: this had zero coverage). */
/**
 * What a refused answer does to the card. A spent budget recorded nothing, so
 * the card stays answerable (after a top-up) and says the refusal's sentence
 * (FRONT-2937). Anything else means the batch is already answered, superseded
 * or gone: lock the form and explain rather than show a bare error.
 */
export function answerFailure(err: unknown): { lockCard: boolean; tone: 'error' | 'info'; message: string } {
  if (err instanceof BudgetRefusedError) return { lockCard: false, tone: 'error', message: err.message };
  return { lockCard: true, tone: 'info', message: (err as Error).message || 'EDDY already moved on from these questions.' };
}

export function submitKind(body: AnswerBody): 'skip' | 'answers' | 'dismiss' {
  if ('dismiss' in body) return 'dismiss';
  return 'skip' in body ? 'skip' : 'answers';
}

/**
 * Pure — the optimistic answer array shown immediately after a successful
 * submit, BEFORE the tape's own question_answered event confirms it. Must
 * match the backend's exact wording (routes.ts) for stop/skip, or the
 * answered view visibly rewrites itself once the tape event lands.
 * Exported for the same reason as submitKind.
 */
export function optimisticAnswersFor(
  body: Exclude<AnswerBody, { dismiss: true }>,
  questionCount: number,
): CodingQuestionAnswer[] {
  if ('skip' in body) {
    return Array.from({ length: questionCount }, () => ({
      selected: [],
      other: 'Skipped, proceed with your best judgment.',
    }));
  }
  return body.answers;
}

type QuestionsMode = QuestionsItem['mode'];

/**
 * Pure — whether this card still accepts input and can be submitted.
 *
 * Extracted under FRONT-1685 (layer 4) precisely so the rule is testable: the
 * frontend suite runs under plain node with no jsdom, so an inline derivation
 * inside the component could not be exercised at all, and this one had no
 * coverage whatsoever.
 *
 * The signature is the guard. A batch's OWN resolution retires it —
 * answered (including a Skip, which the backend records as an answer, and a
 * build-mode self-answer), stopped, or a submit that genuinely 409'd — and so
 * does the death of the run it belongs to (`runStatus`, below). Whether some
 * NEWER batch has since appeared elsewhere in the tape is deliberately not a
 * parameter, so it cannot be consulted: the bug was `&& interactive` here,
 * which froze an older still-unanswered card's inputs and hid its Submit row
 * the moment a sibling batch showed up.
 *
 * That mattered more than a frozen form. The interview poll
 * (`src/coding/ask-user-tool.ts`) times out after ten minutes WITHOUT
 * resolving the batch, on purpose — it leaves the card live because the
 * answer route starts a continuation run when the user answers it later. So
 * disabling that card removed the only route back into the conversation for
 * it. `runStatus` does not reopen that hole: a timeout winds the run DOWN
 * through a successful tool result, so the tape's boundary is `run_finished`
 * and `deriveStatus` reports 'done', never 'failed'.
 *
 * FRONT-2106 adds `runStatus`, the same move FRONT-1850 made one component
 * over for the composer (`findBlockingQuestionItem`). It takes `deriveStatus`'s
 * ALREADY-DERIVED answer rather than the raw session row, precisely so this
 * is not a second copy of the "is this run dead" rule: `deriveStatus` folds
 * the tape's own run boundary and the session row together, and either signal
 * alone is enough here. That matters because the two arrive by different
 * routes — FRONT-2106's backend half puts a `run_failed` boundary on the tape
 * (`src/coding/store.ts`'s `failCodingSessions`), while the row status is what
 * a client that has not replayed the tape sees first.
 *
 * Why it is needed at all, given that boundary: `findOpenBatchOrBoundary`
 * short-circuits on it and so the composer and the status affordance recover
 * from the tape alone, but THIS predicate is per-item — it only ever sees one
 * batch's own fields, and a `run_failed` event elsewhere on the tape is
 * invisible to it. Without `runStatus` the card went on rendering live radios
 * and an enabled Submit against a run that no longer existed, and the user
 * discovered it was dead only by being refused ("These questions were already
 * answered", `src/coding/routes.ts`).
 *
 * `runStatus` is REQUIRED, not optional. An optional one would silently
 * default every un-updated call site back to treating a dead run as live —
 * the exact bug this closes — so the compiler is made to point at each one.
 */
export function isQuestionCardPending(input: {
  /** The tape's answers, or the optimistic local copy — either retires it. */
  answers: unknown[] | null | undefined;
  /** The tape said stopped, or the interview bar's stop just succeeded
   *  (FRONT-2478). */
  stopped: boolean;
  /** FRONT-2661: the tape said dismissed, or this card's Dismiss just
   *  succeeded. Closes the card without answers, like a stop. */
  dismissed: boolean;
  /** A submit came back 409: the run already consumed or superseded it. */
  superseded: boolean;
  /** `deriveStatus(session.status, events)` — NOT the raw session row.
   *  `undefined` only while the session's first status is still unknown, and
   *  an unknown run is treated as live (the card keeps working). */
  runStatus: CodingStatus | undefined;
}): boolean {
  if (input.runStatus === 'failed') return false;
  return !input.answers && !input.stopped && !input.dismissed && !input.superseded;
}

/** Pure — Skip only ever made sense on a real interview discovery question,
 *  never on the confirmsBuild go/adjust gate or a sparing 'build'-mode
 *  question — the go/adjust question is itself tagged mode:'interview'
 *  (FRONT-917), so confirmsBuild has to be checked explicitly here too. */
export function showSkipButton(mode: QuestionsMode, confirmsBuild: boolean): boolean {
  return mode === 'interview' && !confirmsBuild;
}

/**
 * Pure — FRONT-1307. Whether the card offers its auto-added free-text row.
 * Keyed on the asking tool's own capability, NOT on "is this a gate": the
 * publish gate can only publish or not, so a typed answer had to be discarded
 * — "Yeah publish only for me" came back to the user as "I'll leave it
 * unpublished for now". The go/adjust gate is also a gate and deliberately
 * keeps Other, because "adjust, and here's what to change" is an answer it
 * handles correctly: the words reach the model verbatim and withholding build
 * consent is the right outcome. Free text is only removed where it cannot be
 * acted on.
 */
export function showOtherOption(requiresOption: boolean): boolean {
  return !requiresOption;
}

/**
 * Pure -- the status label shown in the card header (follow-up found live
 * 2026-09-04 while testing FRONT-1708). An older batch this session already
 * retired server-side (FRONT-715's cancelPendingBatches -- any newer
 * ask_user call unconditionally cancels every still-pending prior batch for
 * the session) used to read as a plain "pending" -- visually identical to a
 * genuinely still-open card, discoverable only by clicking through and
 * getting a 409. `interactive` (this item is the newest 'questions' item in
 * the transcript) is a SAFE signal for this LABEL, unlike for
 * isQuestionCardPending's form-gating above (see its own comment): by the
 * time a card is no longer the newest, its batch has unconditionally already
 * been cancelled server-side, so there is no live state this label could get
 * wrong by trusting it -- unlike form interactivity, which must stay
 * clickable for a batch whose OWN resolution (not a sibling's) hasn't
 * happened yet.
 */
export function questionCardStatusLabel(input: {
  answered: boolean;
  selfAnswered: boolean;
  stopped: boolean;
  /** FRONT-2661 — the card's own Dismiss. */
  dismissed: boolean;
  interactive: boolean;
  /** FRONT-2106 — same value `isQuestionCardPending` gets. Ordered BELOW the
   *  batch's own resolutions on purpose: a card the user really did answer
   *  before the backend died still reads "answered", because that is what
   *  happened. Only a card nothing ever resolved takes the run's label. */
  runStatus: CodingStatus | undefined;
}): string {
  if (input.answered) return input.selfAnswered ? 'auto-answered' : 'answered';
  if (input.stopped) return 'stopped';
  if (input.dismissed) return 'dismissed';
  // FRONT-2106: without this an unresolved card on a dead run read "waiting
  // for you" while having no Submit row left to act on — a worse lie than the
  // enabled-Submit bug it replaced, since it names the user as the blocker.
  if (input.runStatus === 'failed') return 'run failed';
  return input.interactive ? 'waiting for you' : 'superseded';
}

/**
 * The coding transcript's ask_user question card (FRONT-707). Unlike chat's
 * QuestionsCard (which polls /api/questions/current), state comes straight
 * from the tape: the item is pending until a question_answered event lands
 * for its batch — so this card has no query of its own, only the submit.
 *
 * Reuses the chat card's option views (PendingView/AnsweredView) so both
 * surfaces render questions identically.
 */
export function CodingQuestionsCard({
  item,
  interactive,
  runStatus,
  onAnswer,
  stoppedLocally = false,
}: {
  item: QuestionsItem;
  /** Only the newest pending card accepts input — older ones are history. */
  interactive: boolean;
  /** `deriveStatus`'s verdict for the whole session (FRONT-2106) — a card
   *  whose run is dead cannot be answered, whatever its own fields say. */
  runStatus: CodingStatus | undefined;
  onAnswer: (batchId: number, body: AnswerBody) => Promise<unknown>;
  /** FRONT-2478: the interview bar stopped this batch and the POST
   *  succeeded, but its stopped tape event has not landed yet (a live run's
   *  blocking tool appends it on its next poll). */
  stoppedLocally?: boolean;
}) {
  const [selections, setSelections] = useState<Record<number, string[]>>({});
  const [others, setOthers] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState<'answers' | 'skip' | 'dismiss' | null>(null);
  // FRONT-2661: the Dismiss POST succeeded. With a live run the dismissed
  // tape event only lands on the blocking tool's next poll, so the card
  // closes on this until then (same move as `stoppedLocally`).
  const [dismissedLocally, setDismissedLocally] = useState(false);
  // Optimistic: once the POST succeeds we render the answered view with the
  // local answers until the tape's question_answered event confirms.
  const [localAnswers, setLocalAnswers] = useState<CodingQuestionAnswer[] | null>(null);
  // A late/rejected submit (the run already consumed or superseded this batch)
  // locks the form — leaving it interactive would invite a second dead submit.
  const [superseded, setSuperseded] = useState(false);

  // The tape is truth: once its question_answered event lands, show those
  // answers, not the optimistic local copy (prevents a silent divergence if
  // the build-mode self-answer beat the user).
  const answers = item.answers ?? localAnswers;
  const answered = !!answers;
  // FRONT-1667: a stop closes the card WITHOUT answers. The stop itself is
  // sent from the interview bar (FRONT-2478); with no run active its POST
  // returns the stopped event inline, but with a live run the tape event
  // only arrives on the blocking tool's next poll, so the bar's success is
  // honoured locally until then — otherwise Submit/Skip stay live on a batch
  // the server already cancelled and a click there dead-ends in a 409.
  const stopped = item.outcome === 'stopped' || stoppedLocally;
  const dismissed = item.outcome === 'dismissed' || dismissedLocally;
  const selfAnswered = item.outcome === 'self_answered';
  // FRONT-1685: `interactive` (this batch is the newest) used to gate
  // `pending` outright — the moment ANY newer question appeared elsewhere in
  // the tape, this card's still-unanswered draft was disabled and its Submit
  // row hidden, with no way to send it (this card never unmounts — it's
  // keyed on the stable `item.seq`, so the draft itself survived in memory,
  // just stranded behind a frozen, unsubmittable form). The batch's OWN
  // resolution — answered, stopped, or a submit that genuinely 409s — is
  // what should retire it, not a sibling batch appearing. `interactive` is
  // now only used below to keep the "waiting for you" tone/label on the one
  // batch Eddy is actually blocked on; the existing 409 handling in submit()
  // is what protects a submit against real staleness. The rule itself lives in
  // isQuestionCardPending above, whose signature cannot see `interactive`.
  const pending = isQuestionCardPending({ answers, stopped, dismissed, superseded, runStatus });

  const canSubmit = useMemo(
    () =>
      item.questions.every((q, i) => {
        const sel = selections[i] ?? [];
        if (sel.includes(OTHER)) return (others[i] ?? '').trim().length > 0;
        return sel.length > 0;
      }),
    [item.questions, selections, others],
  );

  function toggle(qi: number, label: string, multi: boolean) {
    setSelections((prev) => {
      const cur = prev[qi] ?? [];
      if (multi) {
        return {
          ...prev,
          [qi]: cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label],
        };
      }
      return { ...prev, [qi]: [label] };
    });
  }

  async function submit(body: AnswerBody) {
    setSubmitting(submitKind(body));
    try {
      const result = (await onAnswer(item.batchId, body)) as
        | { warning?: string; runStarted?: boolean }
        | undefined;
      // Match the server's exact payload so the answered view doesn't
      // visibly rewrite when the tape event confirms.
      if ('dismiss' in body) setDismissedLocally(true);
      else setLocalAnswers(optimisticAnswersFor(body, item.questions.length));
      // Backend answered but could NOT start the continuation run (capacity,
      // etc.) — surface it instead of leaving the user thinking Eddy resumed.
      if (result?.warning) toast.warning(result.warning, { duration: 8000 });
    } catch (err) {
      const failure = answerFailure(err);
      if (failure.lockCard) setSuperseded(true);
      if (failure.tone === 'error') toast.error(failure.message);
      else toast.info(failure.message);
    } finally {
      setSubmitting(null);
    }
  }

  function handleSubmit() {
    const collected: CodingQuestionAnswer[] = item.questions.map((q, i) => {
      const sel = selections[i] ?? [];
      const hasOther = sel.includes(OTHER);
      const ans: CodingQuestionAnswer = { selected: sel.filter((s) => s !== OTHER) };
      if (hasOther) ans.other = (others[i] ?? '').trim();
      return ans;
    });
    void submit({ answers: collected });
  }

  let headerIcon = <ChatCircleText size={14} weight="fill" className="text-primary" />;
  if (answered) headerIcon = <CheckCircle size={14} weight="fill" className="text-success" />;
  else if (stopped) headerIcon = <HandPalm size={14} weight="fill" className="text-muted-foreground" />;
  else if (dismissed) headerIcon = <XCircle size={14} weight="fill" className="text-muted-foreground" />;

  const tone = answered
    ? 'border-success/40 bg-success/5'
    : pending && interactive
      ? 'border-primary/40 bg-primary/5'
      : pending
        ? 'border-muted bg-muted/30'
        : 'border-muted bg-muted/30 opacity-80';

  return (
    // FRONT-1786: the card's own options are Radix radios/checkboxes and are
    // reachable by role, but a session holds several cards at once — a STOPPED
    // card still renders its unanswered options — so an unscoped
    // `getByRole('radio')` matches those leftovers too and trips strict mode.
    // This marks the one card a browser spec should answer.
    //
    // `pending` ALONE is not that card. Per FRONT-1685 above it is deliberately
    // not gated on `interactive`, so a batch the server already retired through
    // cancelPendingBatches — which sends no tape event, and whose `superseded`
    // only flips after a submit actually 409s — is still `pending` on the
    // client while its header reads "superseded". Two cards would then carry
    // this attribute and a strict-mode locator would match both. `interactive`
    // is `item.seq === newestQuestionSeq`, so the pair is exactly one card.
    <div
      className={`border rounded-lg ${tone}`}
      data-testid={pending && interactive ? 'coding-question-card-pending' : undefined}
    >
      <div className="px-3 py-2 border-b border-inherit flex items-center gap-2">
        {headerIcon}
        <span className="text-xs font-medium">
          {item.questions.length === 1 ? '1 question' : `${item.questions.length} questions`}
        </span>
        <span className="ml-auto text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
          {questionCardStatusLabel({ answered, selfAnswered, stopped, dismissed, interactive, runStatus })}
        </span>
      </div>
      {selfAnswered && (
        <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
          You were away, so EDDY went with its recommended options — easy to change later.
        </div>
      )}
      {stopped && (
        <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
          Guided questions are now off for this session — these were left unanswered.
        </div>
      )}
      {dismissed && !stopped && !answered && (
        <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
          You dismissed these questions. EDDY won&apos;t act on them.
        </div>
      )}
      <div className="px-3 py-3 space-y-4">
        {item.questions.map((q, qi) => (
          <div key={qi} className="space-y-2">
            <div className="flex items-center gap-2">
              {q.header && (
                <Badge variant="secondary" className="text-[length:var(--text-2xs)] font-medium">
                  {q.header}
                </Badge>
              )}
              <span className="text-xs font-medium">{q.question}</span>
            </div>
            {answers?.[qi] ? (
              <AnsweredView answer={answers[qi]} />
            ) : (
              <PendingView
                q={q}
                qi={item.batchId * 10 + qi}
                selected={selections[qi] ?? []}
                otherText={others[qi] ?? ''}
                disabled={!pending || submitting !== null}
                allowOther={showOtherOption(item.requiresOption ?? false)}
                onToggle={(label) => toggle(qi, label, q.multiSelect)}
                onOtherChange={(v) => setOthers((p) => ({ ...p, [qi]: v }))}
              />
            )}
          </div>
        ))}
      </div>
      {pending && (
        <div className="px-3 py-2 border-t border-inherit flex items-center justify-end gap-2">
          {/* FRONT-1160: Skip is an escape hatch for a real interview question
              and never shows on the go/adjust confirmation ("Ready for me to
              build this?", confirmsBuild per FRONT-917). FRONT-2478: the
              other hatch, the "stop asking" exit, no longer lives on
              ANY card — it is the Builder's interview bar above the composer,
              where it stays in view however long the card is. It had also
              shown on build-mode forks, which survive a stop on purpose
              (FRONT-1786), so pressing it there stopped nothing visible. */}
          <div className="flex items-center gap-2">
            {/* FRONT-2661: every card can be closed without an answer —
                build-mode forks, the go/adjust gate and consent cards had no
                way out but Submit. Cancels this card only; guided build stays
                on, and the run ends so EDDY cannot act on a guess. */}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={submitting !== null}
              onClick={() => void submit({ dismiss: true })}
              tooltip="Close these questions without answering. EDDY won't act on them"
            >
              {submitting === 'dismiss' && <CircleNotch size={12} className="animate-spin mr-1" />}
              Dismiss
            </Button>
            {showSkipButton(item.mode, item.confirmsBuild ?? false) && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={submitting !== null}
                onClick={() => void submit({ skip: true })}
                tooltip="Skip this question, EDDY proceeds with its best judgment"
              >
                {submitting === 'skip' ? (
                  <CircleNotch size={12} className="animate-spin mr-1" />
                ) : (
                  <FastForward size={12} className="mr-1" />
                )}
                Skip, you decide
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              disabled={!canSubmit || submitting !== null}
              onClick={handleSubmit}
            >
              {submitting === 'answers' ? (
                <CircleNotch size={12} className="animate-spin mr-1" />
              ) : (
                <PaperPlaneTilt size={12} className="mr-1" />
              )}
              Submit answers
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
